import { ArrowLeftRight, BadgeCheck, Briefcase, Building, Check, Clock, ExternalLink, Link, MapPin, Store, Trash2, User, Users, X } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import BONavigation from "../../../components/admin/BONavigation";
import CityAutocomplete from "../../../components/fields/CityAutocomplete";
import {
    getEnrichmentSuggestions,
    approveEnrichmentSuggestion,
    rejectEnrichmentSuggestion,
    purgeEnrichmentSuggestion,
    getEnrichmentStatsByType,
    getImportBatches,
    EnrichmentSuggestion,
    EnrichmentTypeStats,
} from "../../../interfaces/events";
import TodoEmailEnrichment from "../bo/TodoEmailEnrichment";
import { useDebouncedEffect } from "../../../hooks/useDebouncedEffect";

const ENRICHMENT_TYPE_LABELS: Record<string, string> = {
    linkedin: "LinkedIn",
    google_place: "Google Maps",
    google_place_DFS: "Google Maps DFS",
    website: "Website",
    siren: "SIREN",
    company_email: "Email",
    company_linked: "Entreprise liee",
    image: "Image",
    email: "Email",
    email_personal: "Email personnel",
    instagram: "Instagram",
    facebook: "Facebook",
    twitter: "Twitter",
    youtube: "YouTube",
    tiktok: "TikTok",
    osm_place: "OpenStreetMap",
    datatourisme_place: "DATAtourisme",
    website_crawl: "Lecture du site",
};

const FILTER_TABS = [
    { key: "", label: "Tous" },
    { key: "linkedin", label: "Founder LinkedIn" },
    // { key: "google_place", label: "Google Maps" },
    { key: "google_place_DFS", label: "Google Maps DFS" },
    { key: "instagram", label: "Instagram" },
    { key: "facebook", label: "Facebook" },
    { key: "twitter", label: "Twitter" },
    { key: "youtube", label: "YouTube" },
    { key: "tiktok", label: "TikTok" },
    { key: "osm_place", label: "OpenStreetMap" },
    { key: "datatourisme_place", label: "DATAtourisme" },
    { key: "website_crawl", label: "Lecture du site" },
    { key: "__email_todo__", label: "Emails" },
];

const CONFIDENCE_FILTERS = [
    { key: "", label: "Toutes", inactive: "bg-white text-gray-700 border-gray-300", active: "bg-gray-800 text-white border-gray-800" },
    { key: "high", label: "Haute", inactive: "bg-green-50 text-green-700 border-green-300", active: "bg-green-600 text-white border-green-600" },
    { key: "medium", label: "Moyenne", inactive: "bg-orange-50 text-orange-700 border-orange-300", active: "bg-orange-500 text-white border-orange-500" },
    { key: "low", label: "Faible", inactive: "bg-red-50 text-red-700 border-red-300", active: "bg-red-600 text-white border-red-600" },
];

const CONFIDENCE_BADGE: Record<string, string> = {
    high: "bg-green-100 text-green-700 border-green-300",
    medium: "bg-orange-100 text-orange-700 border-orange-300",
    low: "bg-red-100 text-red-700 border-red-300",
    unverified: "bg-gray-100 text-gray-500 border-gray-200",
};

function extractOptionLabel(option: any, type: string): string {
    // Helper to safely extract string value
    const getString = (val: any): string | null => {
        if (typeof val === 'string') return val;
        if (val && typeof val === 'object' && val.title && typeof val.title === 'string') return val.title;
        return null;
    };

    if (type === "linkedin") {
        // Handle nested structure: option.url.{url, title, snippet}
        const linkedinData = option.url && typeof option.url === 'object' ? option.url : option;
        
        // Parse name from title (e.g., "Hubert Tombeur - IT Analyst" -> "Hubert Tombeur")
        if (linkedinData.title && typeof linkedinData.title === 'string') {
            const parts = linkedinData.title.split(' - ');
            return parts[0].trim();
        }
        
        // Fallback to URL slug parsing
        if (linkedinData.url && typeof linkedinData.url === 'string') {
            const match = linkedinData.url.match(/linkedin\.com\/in\/([^/?]+)/);
            if (match) {
                const slug = match[1];
                const parts = slug.split('-');
                return parts.map((p: string) => p.charAt(0).toUpperCase() + p.slice(1)).join(' ');
            }
        }
        
        return getString(option.name) || getString(option.fullName) || 
               (typeof option.url === 'string' ? option.url : null) || "Profil LinkedIn";
    }
    if (type === "google_place") {
        return getString(option.display_name) || getString(option.name) ||
               getString(option.formatted_address) || "Lieu Google";
    }
    if (type === "google_place_DFS") {
        return getString(option.title) || getString(option.name) ||
               getString(option.address) || "Lieu Google";
    }
    if (type === "website") {
        return getString(option.domain) || 
               (typeof option.url === 'string' ? option.url : null) || "Site web";
    }
    if (type === "company_linked") {
        return getString(option.companyName) || getString(option.name) || "Entreprise";
    }
    // OSM / DATAtourisme place: name, address, then the contacts it would write.
    if (type === "osm_place" || type === "datatourisme_place") {
        const address = [option.street, [option.postcode, option.city].filter(Boolean).join(" ")].filter(Boolean).join(", ");
        const contacts = [option.phone, option.website, option.email].filter(Boolean).join(" · ");
        return [getString(option.name) || "Lieu", address, contacts].filter(Boolean).join(" — ");
    }
    // Our own read of the site: what it found.
    if (type === "website_crawl") {
        const parts = [
            ...(Array.isArray(option.emails) ? option.emails.slice(0, 3) : []),
            ...(Array.isArray(option.phones) ? option.phones.slice(0, 2) : []),
            ...Object.keys(option.socials || {}),
        ];
        return parts.length ? parts.join(" · ") : "Rien trouvé";
    }
    
    const safeName = getString(option.name) || getString(option.label);
    if (safeName) return safeName;
    
    // Last resort: stringify safely
    try {
        return JSON.stringify(option).slice(0, 40);
    } catch {
        return "Option";
    }
}

function extractOptionUrl(option: any, type: string): string | null {
    // Helper to safely extract URL
    const getUrl = (val: any): string | null => {
        if (typeof val === 'string') return val;
        if (val && typeof val === 'object' && val.url && typeof val.url === 'string') return val.url;
        return null;
    };

    if (type === "linkedin") {
        // Handle nested structure: option.url.{url, title, snippet}
        if (option.url && typeof option.url === 'object' && option.url.url && typeof option.url.url === 'string') {
            return option.url.url;
        }
        return getUrl(option.url) || getUrl(option.linkedin_url) || null;
    }
    if (type === "google_place") {
        return getUrl(option.google_maps_url) || null;
    }
    if (type === "google_place_DFS") {
        if (option.place_id) return `https://www.google.com/maps/place/?q=place_id:${option.place_id}`;
        return getUrl(option.url) || null;
    }
    if (type === "website") {
        const url = getUrl(option.url);
        if (url) return url;
        if (typeof option.domain === 'string') return `https://${option.domain}`;
        return null;
    }
    if (type === "osm_place" && typeof option.sourceId === "string") {
        return `https://www.openstreetmap.org/${option.sourceId}`;
    }
    if (type === "datatourisme_place" && typeof option.sourceId === "string") {
        return option.sourceId;
    }
    if (type === "website_crawl") {
        return getUrl(option.finalUrl) || getUrl(option.url) || null;
    }
    if (["instagram", "facebook", "twitter", "linkedin", "youtube", "tiktok"].includes(type)) {
        return getUrl(option.url) || (typeof option === 'string' ? option : null);
    }
    return getUrl(option.url) || null;
}

function getEnrichmentBadge(type: string, target: string): string {
    const typeLabel = ENRICHMENT_TYPE_LABELS[type] || type.toUpperCase();
    if (target === "person") return `PROPRIETAIRE · ${typeLabel.toUpperCase()}`;
    return typeLabel.toUpperCase();
}

function getDaysAgo(dateStr?: string): string | null {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return null;
    const days = Math.floor((Date.now() - date.getTime()) / (1000 * 60 * 60 * 24));
    if (days === 0) return "aujourd'hui";
    if (days === 1) return "il y a 1 jour";
    return `il y a ${days} jours`;
}


export default function EnrichmentSuggestions() {
    const navigate = useNavigate();
    const [suggestions, setSuggestions] = useState<EnrichmentSuggestion[]>([]);
    const [loading, setLoading] = useState(true);
    const [activeFilter, setActiveFilter] = useState("");
    const [confidenceFilter, setConfidenceFilter] = useState("");
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    // How many matching records the active date range is excluding. The date filter applies
    // to detectedAt (when the business event happened), not to when the enrichment was
    // fetched — so recent results for older events fall outside the default 7-day window.
    const [hiddenByDate, setHiddenByDate] = useState(0);
    const [statsByType, setStatsByType] = useState<Record<string, EnrichmentTypeStats>>({});
    const [selectedOptions, setSelectedOptions] = useState<Record<string, number>>({});
    const [actionLoading, setActionLoading] = useState<string | null>(null);
    const [showOnlyWithPeople, _setShowOnlyWithPeople] = useState(false);
    const [sanitaryOnly, setSanitaryOnly] = useState(false);
    const [cityFilter, setCityFilter] = useState("");
    const [showIframe, setShowIframe] = useState<Record<string, boolean>>({});
    const [dateFrom, setDateFrom] = useState(() => { const d = new Date(); d.setDate(d.getDate() - 7); return d.toLocaleDateString("en-CA"); });
    const [dateTo, setDateTo] = useState(() => new Date().toLocaleDateString("en-CA"));
    const [importBatch, setImportBatch] = useState("");
    const [importBatches, setImportBatches] = useState<string[]>([]);
    const [linkedinChecks, setLinkedinChecks] = useState<string[]>([]);
    const fetchCounterRef = useRef(0);

    useEffect(() => {
        getImportBatches().then((res) => setImportBatches(res.batches)).catch(() => {});
    }, []);


    const fetchStatsByType = async (from?: string, to?: string, eventType?: string) => {
        try {
            const params: any = {};
            if (from) params.from = from;
            if (to) params.to = to;
            if (eventType) params.eventType = eventType;
            const res = await getEnrichmentStatsByType(params);
            setStatsByType(res.byType);
        } catch (err) {
            console.error("Failed to fetch stats by type:", err);
        }
    };

    const fetchSuggestions = async () => {
        if (activeFilter === "__email_todo__") return;
        const requestId = ++fetchCounterRef.current;
        setSuggestions([]);
        setLoading(true);
        try {
            const params: any = { page, limit: 50 };
            if (activeFilter) params.type = activeFilter;
            if (showOnlyWithPeople) params.withPeople = true;
            if (sanitaryOnly) params.eventType = "hygiene_inspection";
            if (cityFilter.trim()) params.city = cityFilter.trim();
            if (dateFrom || dateTo) {
                if (dateFrom) params.from = dateFrom;
                if (dateTo) params.to = dateTo;
            }
            if (importBatch) params.importBatch = importBatch;
            if (confidenceFilter) params.confidenceFilter = confidenceFilter;
            if (activeFilter === "linkedin" && linkedinChecks.length > 0) params.linkedinChecks = linkedinChecks.join(",");
            const res = await getEnrichmentSuggestions(params);
            if (requestId !== fetchCounterRef.current) return;
            setSuggestions(res.data);
            setTotal(res.meta.total);
            setHiddenByDate(res.meta.hiddenByDate ?? 0);
        } catch (err) {
            if (requestId !== fetchCounterRef.current) return;
            console.error("Failed to fetch suggestions:", err);
        } finally {
            if (requestId === fetchCounterRef.current) setLoading(false);
        }
    };

    useDebouncedEffect(() => {
        fetchStatsByType(dateFrom, dateTo, sanitaryOnly ? "hygiene_inspection" : undefined);
    }, [dateFrom, dateTo, sanitaryOnly]);

    useDebouncedEffect(() => {
        fetchSuggestions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, activeFilter, sanitaryOnly, cityFilter, dateFrom, dateTo, importBatch, confidenceFilter, linkedinChecks]);

    const getSuggestionKey = (s: EnrichmentSuggestion, idx: number) =>
        `${s.eventId}-${s.enrichmentType}-${s.personId || "event"}-${idx}`;

    const handleApprove = async (suggestion: EnrichmentSuggestion, idx: number) => {
        const key = getSuggestionKey(suggestion, idx);
        const selectedIdx = selectedOptions[key] ?? 0;
        let selectedOption = suggestion.options[selectedIdx];

        // Flatten nested URL structure for LinkedIn enrichments
        if (suggestion.enrichmentType === "linkedin" && selectedOption?.url && typeof selectedOption.url === 'object') {
            // Extract the URL string from the nested structure
            const urlString = selectedOption.url.url;
            selectedOption = {
                ...selectedOption,
                ...selectedOption.url, // Flatten: merge url object properties to top level
                url: urlString, // Ensure url is the string value, not the nested object
            };
        }

        setActionLoading(key);
        try {
            await approveEnrichmentSuggestion(suggestion.eventId, {
                enrichmentType: suggestion.enrichmentType,
                selectedOption,
                personId: suggestion.personId || undefined,
            });
            setSuggestions((prev) => prev.filter((_, i) => i !== idx));
        } catch (err) {
            console.error("Failed to approve:", err);
        } finally {
            setActionLoading(null);
        }
    };

    const handleReject = async (suggestion: EnrichmentSuggestion, idx: number) => {
        const key = getSuggestionKey(suggestion, idx);
        setActionLoading(key);
        try {
            await rejectEnrichmentSuggestion(suggestion.eventId, {
                enrichmentType: suggestion.enrichmentType,
                personId: suggestion.personId || undefined,
            });
            setSuggestions((prev) => prev.filter((_, i) => i !== idx));
        } catch (err) {
            console.error("Failed to reject:", err);
        } finally {
            setActionLoading(null);
        }
    };

    const handlePurge = async (suggestion: EnrichmentSuggestion, idx: number) => {
        const key = getSuggestionKey(suggestion, idx);
        setActionLoading(key);
        try {
            await purgeEnrichmentSuggestion(suggestion.eventId, {
                enrichmentType: suggestion.enrichmentType,
                personId: suggestion.personId || undefined,
            });
            setSuggestions((prev) => prev.filter((_, i) => i !== idx));
        } catch (err) {
            console.error("Failed to purge:", err);
        } finally {
            setActionLoading(null);
        }
    };

    const filteredSuggestions = suggestions;


    return (
        <div className="min-h-screen bg-[#F5F0EB]">
            <div className="p-6 pb-0">
                <BONavigation />
                <div className="flex items-center justify-between mt-4 mb-2">
                    <button
                        onClick={() => navigate("/bo/enrichments/reviewed")}
                        className="flex items-center gap-2 px-2 py-2 text-xs font-medium text-black border rounded-sm"
                    >
                        <Clock className="h-4 w-4" />
                        Voir l'historique
                    </button>
                </div>
            </div>

            <style>{`
                @keyframes enrichment-progress {
                    0% { transform: translateX(-100%); }
                    100% { transform: translateX(400%); }
                }
            `}</style>

            {/* Sticky header + progress bar */}
            <div className="sticky top-0 z-10 bg-white border-b border-gray-200">
                <div className="px-6">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-2">
                        {/* Filter tabs */}
                        <div className="flex gap-1.5 flex-wrap items-center">
                            {FILTER_TABS.map((tab) => {
                                const isActive = activeFilter === tab.key;
                                const typeStats = tab.key === ""
                                    ? null
                                    : tab.key === "__email_todo__"
                                    ? (statsByType["website_content_OTS"] ?? null)
                                    : statsByType[tab.key];
                                const summed = tab.key === ""
                                    ? Object.values(statsByType).reduce(
                                        (acc, s) => ({
                                            pending: acc.pending + s.pending,
                                            done: acc.done + s.done,
                                            retry: acc.retry + s.retry,
                                            approved: acc.approved + s.approved,
                                        }),
                                        { pending: 0, done: 0, retry: 0, approved: 0 }
                                      )
                                    : null;
                                const s = typeStats ?? summed;
                                return (
                                    <button
                                        key={tab.key}
                                        onClick={() => { setActiveFilter(tab.key); setPage(1); setConfidenceFilter(""); setLinkedinChecks([]); }}
                                        className={`flex flex-col items-start px-3 py-2 text-xs font-medium border transition-colors min-w-[90px] ${
                                            isActive
                                                ? "bg-orange-500 text-white border-orange-500"
                                                : "bg-white text-gray-700 border-gray-300 hover:border-orange-400"
                                        }`}
                                    >
                                        <span className="font-semibold mb-1">{tab.label}</span>
                                        {s && (
                                            <span className="flex flex-col gap-0.5 text-[10px] font-medium w-full">
                                                <span className={`flex items-center justify-between gap-1 ${isActive ? "text-orange-100" : "text-gray-400"}`}>
                                                    <span>En attente</span>
                                                    <span className="font-semibold">{s.pending}</span>
                                                </span>
                                                <span className={`flex items-center justify-between gap-1 ${isActive ? "text-yellow-200" : "text-yellow-600"}`}>
                                                    <span>À valider</span>
                                                    <span className="font-semibold">{s.done}</span>
                                                </span>
                                                <span className={`flex items-center justify-between gap-1 ${isActive ? "text-red-200" : "text-red-500"}`}>
                                                    <span>Retry</span>
                                                    <span className="font-semibold">{s.retry}</span>
                                                </span>
                                                <span className={`flex items-center justify-between gap-1 ${isActive ? "text-green-200" : "text-green-600"}`}>
                                                    <span>Approuvé</span>
                                                    <span className="font-semibold">{s.approved}</span>
                                                </span>
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                            <div className="w-px h-4 bg-gray-300 mx-0.5" />
                            <button
                                onClick={() => { setSanitaryOnly(v => !v); setPage(1); }}
                                className={`px-3 py-1 text-xs font-medium border transition-colors ${
                                    sanitaryOnly
                                        ? "bg-red-600 text-white border-red-600"
                                        : "bg-white text-gray-700 border-gray-300 hover:border-red-400"
                                }`}
                            >
                                🏥 Contrôles sanitaires
                            </button>
                        </div>
                    </div>

                    {/* Checks filter — LinkedIn only */}
                    {activeFilter === "linkedin" && (
                        <div className="flex items-center gap-1.5 mb-2">
                            <span className="text-xs text-gray-500">Signaux :</span>
                            {([
                                { key: "ownerRole", label: "Rôle owner" },
                                { key: "restaurantIndustry", label: "Secteur restau" },
                                { key: "nameMatched", label: "Nom trouvé" },
                                { key: "cityMatched", label: "Ville" },
                                { key: "franchiseMatched", label: "Franchise" },
                            ] as { key: string; label: string }[]).map((c) => {
                                const active = linkedinChecks.includes(c.key);
                                return (
                                    <button
                                        key={c.key}
                                        onClick={() => {
                                            setLinkedinChecks(prev =>
                                                active ? prev.filter(k => k !== c.key) : [...prev, c.key]
                                            );
                                            setPage(1);
                                        }}
                                        className={`px-2 py-0.5 text-xs font-medium border transition-colors ${
                                            active
                                                ? "bg-green-700 text-white border-green-700"
                                                : "bg-white text-gray-600 border-gray-200 hover:border-gray-400"
                                        }`}
                                    >
                                        {c.label}
                                    </button>
                                );
                            })}
                        </div>
                    )}

                    {/* Confidence filter — Google Maps DFS only */}
                    {["google_place_DFS", "osm_place", "datatourisme_place"].includes(activeFilter) && (
                        <div className="flex items-center gap-1.5 mb-2">
                            <span className="text-xs text-gray-500">Confiance IA :</span>
                            {CONFIDENCE_FILTERS.map((f) => (
                                <button
                                    key={f.key}
                                    onClick={() => { setConfidenceFilter(f.key); setPage(1); }}
                                    className={`px-2 py-0.5 text-xs font-medium border transition-colors ${
                                        confidenceFilter === f.key ? f.active : f.inactive
                                    }`}
                                >
                                    {f.label}
                                </button>
                            ))}
                        </div>
                    )}

                    {/* Loading progress bar */}
                    {loading && activeFilter !== "__email_todo__" && (
                        <div className="absolute bottom-0 left-0 right-0 h-0.5 overflow-hidden bg-gray-100">
                            <div
                                className="h-full w-1/3 bg-orange-400"
                                style={{ animation: "enrichment-progress 1.2s ease-in-out infinite" }}
                            />
                        </div>
                    )}

                    {/* Date range + city filter (hidden for email todo tab) */}
                    {activeFilter !== "__email_todo__" && <><div className="mb-0" />
                    {/* Date range + city filter */}
                    <div className="mt-3 flex flex-wrap items-center gap-2 pb-3">
                        <div className="flex items-center gap-1 flex-wrap">
                            {[{ key: "", label: "Tous" }, { key: "internal", label: "Interne" }, ...importBatches.map((b) => ({ key: b, label: b }))].map((opt) => (
                                <button
                                    key={opt.key}
                                    onClick={() => { setImportBatch(opt.key); setPage(1); }}
                                    className={`px-2 py-0.5 text-[10px] font-medium border transition-colors ${
                                        importBatch === opt.key
                                            ? "bg-gray-800 text-white border-gray-800"
                                            : "bg-white text-gray-600 border-gray-300 hover:border-gray-400"
                                    }`}
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                        <CityAutocomplete
                            value={cityFilter}
                            onChange={(val) => { setCityFilter(val); setPage(1); }}
                            placeholder="Filtrer par ville..."
                        />
                        <div className="flex items-center gap-1.5">
                            <input
                                type="date"
                                value={dateFrom}
                                onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
                                className="border border-gray-300 text-xs px-2 py-1 text-gray-700 focus:outline-none focus:border-gray-500"
                            />
                            <span className="text-xs text-gray-400">→</span>
                            <input
                                type="date"
                                value={dateTo}
                                onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
                                className="border border-gray-300 text-xs px-2 py-1 text-gray-700 focus:outline-none focus:border-gray-500"
                            />
                            {(dateFrom || dateTo) && (
                                <button
                                    onClick={() => { setDateFrom(""); setDateTo(""); setPage(1); }}
                                    className="text-xs text-gray-400 hover:text-gray-600 px-1"
                                >
                                    ✕
                                </button>
                            )}
                            {hiddenByDate > 0 && (
                                <button
                                    onClick={() => { setDateFrom(""); setDateTo(""); setPage(1); }}
                                    title="Le filtre de date porte sur la date de l'événement (detectedAt), pas sur la date de récupération de l'enrichissement. Cliquer pour retirer le filtre et tout afficher."
                                    className="flex items-center gap-1 text-xs px-2 py-1 border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 transition-colors whitespace-nowrap"
                                >
                                    <span className="font-semibold">{hiddenByDate.toLocaleString("fr-FR")}</span>
                                    masqué{hiddenByDate > 1 ? "s" : ""} par la date — voir tout
                                </button>
                            )}
                        </div>
                        <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-1 flex-wrap">
                                {Array.from({ length: 12 }, (_, i) => {
                                    const d = new Date();
                                    d.setDate(1);
                                    d.setMonth(d.getMonth() - i);
                                    const year = d.getFullYear();
                                    const month = d.getMonth();
                                    const label = d.toLocaleDateString("fr-FR", { month: "short", year: "2-digit" });
                                    const from = `${year}-${String(month + 1).padStart(2, "0")}-01`;
                                    const lastDay = new Date(year, month + 1, 0).getDate();
                                    const to = `${year}-${String(month + 1).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
                                    const isActive = dateFrom === from && dateTo === to;
                                    return (
                                        <button
                                            key={from}
                                            onClick={() => { setDateFrom(from); setDateTo(to); setPage(1); }}
                                            className={`px-2 py-0.5 text-[10px] font-medium border transition-colors ${
                                                isActive
                                                    ? "bg-gray-800 text-white border-gray-800"
                                                    : "bg-white text-gray-600 border-gray-300 hover:border-gray-400"
                                            }`}
                                        >
                                            {label}
                                        </button>
                                    );
                                })}
                            </div>
                            {/* Week sub-picker — shown when a full month is selected */}
                            {(() => {
                                if (!dateFrom || !dateTo) return null;
                                // Detect if a full month is selected
                                const from = new Date(dateFrom);
                                if (from.getDate() !== 1) return null;
                                const year = from.getFullYear();
                                const month = from.getMonth();
                                const lastDay = new Date(year, month + 1, 0).getDate();
                                const expectedTo = `${year}-${String(month + 1).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
                                if (dateTo !== expectedTo) return null;

                                // Build week chunks: 7-day blocks from the 1st
                                const pad = (n: number) => String(n).padStart(2, "0");
                                const mm = String(month + 1).padStart(2, "0");
                                const weeks: { from: string; to: string; label: string }[] = [];
                                let day = 1;
                                let wk = 1;
                                while (day <= lastDay) {
                                    const end = Math.min(day + 6, lastDay);
                                    weeks.push({
                                        from: `${year}-${mm}-${pad(day)}`,
                                        to: `${year}-${mm}-${pad(end)}`,
                                        label: `S${wk}`,
                                    });
                                    day += 7;
                                    wk++;
                                }

                                return (
                                    <div className="flex items-center gap-1">
                                        <span className="text-[10px] text-gray-400">sem :</span>
                                        {weeks.map((w) => {
                                            const isWeekActive = dateFrom === w.from && dateTo === w.to;
                                            return (
                                                <button
                                                    key={w.from}
                                                    onClick={() => { setDateFrom(w.from); setDateTo(w.to); setPage(1); }}
                                                    className={`px-2 py-0.5 text-[10px] font-medium border transition-colors ${
                                                        isWeekActive
                                                            ? "bg-gray-600 text-white border-gray-600"
                                                            : "bg-white text-gray-500 border-gray-200 hover:border-gray-400"
                                                    }`}
                                                >
                                                    {w.label}
                                                </button>
                                            );
                                        })}
                                    </div>
                                );
                            })()}
                        </div>
                    </div>
                    </>}
                </div>
            </div>

            {/* Email enrichment todo (special tab) */}
            {activeFilter === "__email_todo__" && <TodoEmailEnrichment />}

            {/* Content area */}
            <div className="p-4" style={{ display: activeFilter === "__email_todo__" ? "none" : undefined }}>
                {/* Empty state */}
                {!loading && filteredSuggestions.length === 0 && (
                    <div className="text-center py-12 text-gray-500">
                        <p className="text-sm">
                            {confidenceFilter
                                ? "Aucune suggestion pour ce niveau de confiance"
                                : "Aucune suggestion en attente"}
                        </p>
                        <p className="text-xs mt-1">
                            {confidenceFilter
                                ? "Essayez un autre filtre de confiance."
                                : "Les enrichissements avec des options a valider apparaitront ici."}
                        </p>
                    </div>
                )}

                {/* Suggestion cards */}
                <div className="space-y-5">
                    {filteredSuggestions.map((suggestion, idx) => {
                        const key = getSuggestionKey(suggestion, idx);
                        const selectedIdx = selectedOptions[key] ?? 0;
                        const selectedOption = suggestion.options[selectedIdx];
                        const optionUrl = extractOptionUrl(selectedOption, suggestion.enrichmentType);
                        const isLoading = actionLoading === key;

                        return (
                            <div
                                key={key}
                                className="bg-white border border-gray-200 hover:border-gray-300 transition-colors overflow-hidden flex"
                            >
                                {/* Main content */}
                                <div className="flex-1 p-5">
                                    {/* Company header */}
                                    <div className="flex items-start gap-3 mb-4">
                                        <div className="w-10 h-10 bg-gray-100 flex items-center justify-center flex-shrink-0">
                                            <Store className="w-5 h-5 text-gray-600" />
                                        </div>
                                        <div>
                                            <h3 className="font-semibold text-gray-900 text-base flex items-center gap-2">
                                                {suggestion.restaurantName && suggestion.restaurantName !== suggestion.companyName
                                                    ? <>{suggestion.companyName} <span className="font-normal text-gray-500">({suggestion.restaurantName})</span></>
                                                    : suggestion.companyName
                                                }
                                                {suggestion.data?.name && suggestion.data.name !== suggestion.companyName && (
                                                    <span className="text-sm font-normal text-gray-400">
                                                        · {suggestion.data.name}
                                                    </span>
                                                )}
                                            </h3>
                                            {suggestion.address && (
                                                <p className="text-sm text-gray-500 flex items-center gap-1">
                                                    <MapPin className="w-3.5 h-3.5" />
                                                    {suggestion.address}
                                                </p>
                                            )}
                                            {suggestion.siren && (
                                                <p className="text-xs text-gray-400 mt-0.5">
                                                    <a
                                                        href={`https://www.pappers.fr/entreprise/${suggestion.siren}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="hover:text-gray-700 hover:underline"
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        SIRET {suggestion.siren}
                                                        <ExternalLink className="inline w-3 h-3 ml-1 mb-0.5" />
                                                    </a>
                                                </p>
                                            )}
                                        </div>
                                    </div>

                                    {/* Enrichment type badge */}
                                    <div className="mb-3">
                                        {suggestion.target === "person" && suggestion.personName && (
                                            <span className="ml-2 text-sm text-gray-600">
                                                <User className="w-3.5 h-3.5 inline mr-1" />
                                                {suggestion.personName}
                                                {suggestion.personRole && (
                                                    <span className="text-gray-400 ml-1">
                                                        ({suggestion.personRole})
                                                    </span>
                                                )}
                                            </span>
                                        )}
                                    </div>

                                    {/* Options */}
                                    <div className="mb-3">
                                        <div className="flex flex-wrap gap-2">
                                            {suggestion.options.map((option, optIdx) => {
                                                const label = extractOptionLabel(option, suggestion.enrichmentType);
                                                const isSelected = optIdx === selectedIdx;
                                                const checks = suggestion.enrichmentType === "linkedin" ? option.checks : undefined;
                                                const CHECK_LABELS: [string, string][] = [
                                                    ["ownerRole", "Rôle"],
                                                    ["restaurantIndustry", "Secteur"],
                                                    ["nameMatched", "Nom"],
                                                    ["cityMatched", "Ville"],
                                                    ["franchiseMatched", "Franchise"],
                                                ];

                                                return (
                                                    <button
                                                        key={optIdx}
                                                        onClick={() =>
                                                            setSelectedOptions((prev) => ({
                                                                ...prev,
                                                                [key]: optIdx,
                                                            }))
                                                        }
                                                        className={`flex flex-col gap-1 px-3 py-1.5 text-xs font-medium border transition-colors ${
                                                            isSelected
                                                                ? "bg-gray-900 text-white border-gray-900"
                                                                : "bg-white text-gray-700 border-gray-200 hover:border-gray-300"
                                                        }`}
                                                    >
                                                        <span>{label}</span>
                                                        {checks && (
                                                            <span className="flex items-center gap-1">
                                                                {CHECK_LABELS.map(([key, lbl]) => (
                                                                    <span
                                                                        key={key}
                                                                        title={lbl}
                                                                        className={`text-[10px] px-1 rounded ${
                                                                            checks[key]
                                                                                ? isSelected ? "bg-green-700 text-green-200" : "bg-green-100 text-green-700"
                                                                                : isSelected ? "bg-gray-700 text-gray-400" : "bg-gray-100 text-gray-400"
                                                                        }`}
                                                                    >
                                                                        {lbl}
                                                                    </span>
                                                                ))}
                                                            </span>
                                                        )}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    {/* Google Place context panel — Instagram only */}
                                    {suggestion.enrichmentType === "instagram" && suggestion.googlePlace?.data && (() => {
                                        const gp = suggestion.googlePlace!;
                                        const d = gp.data;
                                        const isDFS = gp.type === "google_place_DFS";

                                        const displayName = isDFS
                                            ? (d.title ?? d.name)
                                            : (d.display_name ?? d.name);
                                        const formattedAddress = isDFS ? d.address : d.formatted_address;
                                        const website = d.website ?? d.url;
                                        const phone = isDFS ? d.phone : d.formatted_phone_number;
                                        const ratingValue = isDFS
                                            ? (typeof d.rating?.value === "number" ? d.rating.value : d.rating)
                                            : d.rating;
                                        const reviewCount = isDFS ? d.rating?.votes_count : d.user_ratings_total;

                                        const categories: string[] = isDFS
                                            ? [d.category, ...(Array.isArray(d.additional_categories) ? d.additional_categories : [])].filter(Boolean)
                                            : (Array.isArray(d.types) ? d.types : []);

                                        return (
                                            <div className="mb-3 border border-gray-200 bg-gray-50 p-3 space-y-2">
                                                <p className="text-[9px] font-semibold text-gray-400 uppercase tracking-wide">
                                                    {isDFS ? "Google Place DFS · approuvé" : "Google Place · approuvé"}
                                                </p>

                                                {/* Name + rating */}
                                                <div className="flex items-start justify-between gap-2">
                                                    <p className="text-sm font-semibold text-gray-900">{displayName ?? "—"}</p>
                                                    {ratingValue != null && (
                                                        <div className="flex items-center gap-1 flex-shrink-0">
                                                            <span className="text-yellow-500 text-xs">★</span>
                                                            <span className="text-xs font-semibold text-gray-800">
                                                                {typeof ratingValue === "number" ? ratingValue.toFixed(1) : ratingValue}
                                                            </span>
                                                            {reviewCount != null && (
                                                                <span className="text-[10px] text-gray-400">
                                                                    ({reviewCount.toLocaleString("fr-FR")})
                                                                </span>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Categories / tags */}
                                                {categories.length > 0 && (
                                                    <div className="flex flex-wrap gap-1">
                                                        {categories.map((cat: string, i: number) => (
                                                            <span
                                                                key={i}
                                                                className="px-1.5 py-0.5 text-[10px] bg-white border border-gray-200 text-gray-600"
                                                            >
                                                                {cat}
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}

                                                {/* Address */}
                                                {formattedAddress && (
                                                    <p className="text-xs text-gray-600 flex items-center gap-1">
                                                        <MapPin className="w-3 h-3 text-gray-400 flex-shrink-0" />
                                                        {formattedAddress}
                                                    </p>
                                                )}

                                                {/* Website */}
                                                {website && (
                                                    <p className="text-xs text-gray-600 flex items-center gap-1">
                                                        <Link className="w-3 h-3 text-gray-400 flex-shrink-0" />
                                                        <a
                                                            href={website}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="truncate hover:underline text-gray-700"
                                                            onClick={(e) => e.stopPropagation()}
                                                        >
                                                            {website}
                                                        </a>
                                                    </p>
                                                )}

                                                {/* Phone */}
                                                {phone && (
                                                    <p className="text-xs text-gray-600">📞 {phone}</p>
                                                )}
                                            </div>
                                        );
                                    })()}

                                    {/* URL preview */}
                                    {optionUrl && (
                                        <div className="mb-3">
                                            <div className="flex items-center gap-2 bg-gray-50 px-3 py-2 border border-gray-200">
                                                <Link className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                                <span className="text-xs text-gray-600 truncate flex-1 max-w-xs">
                                                    {optionUrl}
                                                </span>
                                                {suggestion.enrichmentType === "website" && (
                                                    <button
                                                        onClick={() => setShowIframe((prev) => ({ ...prev, [key]: !prev[key] }))}
                                                        className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 font-medium flex-shrink-0 border border-gray-300 px-2 py-0.5 hover:bg-gray-100"
                                                    >
                                                        {showIframe[key] ? "Masquer" : "Prévisualiser"}
                                                    </button>
                                                )}
                                                <a
                                                    href={optionUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="flex items-center gap-1 text-xs text-gray-900 hover:underline font-medium flex-shrink-0"
                                                >
                                                    <ExternalLink className="w-3.5 h-3.5" />
                                                    Open
                                                </a>
                                            </div>
                                            {suggestion.enrichmentType === "website" && showIframe[key] && (
                                                <div className="border border-t-0 border-gray-200 bg-gray-50">
                                                    <iframe
                                                        src={optionUrl}
                                                        title="Website preview"
                                                        className="w-full h-64 block"
                                                        sandbox="allow-scripts allow-same-origin"
                                                        loading="lazy"
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Rich preview for LinkedIn */}
                                    {suggestion.enrichmentType === "linkedin" && selectedOption && (() => {
                                        // Handle nested structure: option.url.{url, title, snippet}
                                        const linkedinData = selectedOption.url && typeof selectedOption.url === 'object' 
                                            ? selectedOption.url 
                                            : selectedOption;
                                        
                                        // Extract name from URL slug (e.g., "huberttombeur" from URL)
                                        const extractNameFromUrl = (url: string): { firstName: string; lastName: string } | null => {
                                            try {
                                                const match = url.match(/linkedin\.com\/in\/([^/?]+)/);
                                                if (match) {
                                                    const slug = match[1];
                                                    // Split by dash or capitalize each part
                                                    const parts = slug.split('-').filter(Boolean);
                                                    if (parts.length >= 2) {
                                                        return {
                                                            firstName: parts[0].charAt(0).toUpperCase() + parts[0].slice(1),
                                                            lastName: parts.slice(1).map((p: string) => p.charAt(0).toUpperCase() + p.slice(1)).join(' ')
                                                        };
                                                    } else if (parts.length === 1) {
                                                        // Single word slug - try to split camelCase or just use as is
                                                        const name = parts[0];
                                                        return {
                                                            firstName: name.charAt(0).toUpperCase() + name.slice(1),
                                                            lastName: ''
                                                        };
                                                    }
                                                }
                                            } catch (e) {
                                                console.error('Error parsing LinkedIn URL:', e);
                                            }
                                            return null;
                                        };

                                        // Parse title field (e.g., "Hubert Tombeur - IT Analyst")
                                        const parseTitle = (title: string): { fullName?: string; headline?: string } => {
                                            const parts = title.split(' - ');
                                            if (parts.length >= 2) {
                                                return {
                                                    fullName: parts[0].trim(),
                                                    headline: parts.slice(1).join(' - ').trim()
                                                };
                                            }
                                            return { fullName: title };
                                        };

                                        const profileUrl = typeof linkedinData.url === 'string' ? linkedinData.url : null;
                                        const titleData = linkedinData.title && typeof linkedinData.title === 'string' 
                                            ? parseTitle(linkedinData.title) 
                                            : {};
                                        const urlName = profileUrl ? extractNameFromUrl(profileUrl) : null;
                                        
                                        // Determine display name (prefer parsed title, fallback to URL parsing)
                                        const displayName = titleData.fullName || 
                                                          (urlName ? `${urlName.firstName} ${urlName.lastName}`.trim() : null) ||
                                                          (typeof selectedOption.name === 'string' && selectedOption.name) || 
                                                          (typeof selectedOption.fullName === 'string' && selectedOption.fullName) || 
                                                          "Profil LinkedIn";
                                        
                                        const headline = titleData.headline || 
                                                        (selectedOption.headline && typeof selectedOption.headline === 'string' ? selectedOption.headline : null);
                                        
                                        const snippet = linkedinData.snippet && typeof linkedinData.snippet === 'string' 
                                            ? linkedinData.snippet 
                                            : (selectedOption.snippet && typeof selectedOption.snippet === 'string' ? selectedOption.snippet : null);

                                        return (
                                            <div className="bg-white border border-gray-200 overflow-hidden mb-3">
                                                {/* LinkedIn-style cover */}
                                                <div className="h-8 bg-gray-100" />
                                                
                                                <div className="px-3 pb-3">
                                                    {/* Profile photo */}
                                                    <div className="flex items-start -mt-4 mb-2">
                                                        <div className="relative">
                                                            <div className="w-10 h-10 bg-gray-100 border-2 border-white flex items-center justify-center">
                                                                {(() => {
                                                                    const initials = displayName.split(' ')
                                                                        .map((word: string) => word[0])
                                                                        .filter(Boolean)
                                                                        .slice(0, 2)
                                                                        .join('')
                                                                        .toUpperCase();
                                                                    return (
                                                                        <span className="text-white text-sm font-semibold">
                                                                            {initials}
                                                                        </span>
                                                                    );
                                                                })()}
                                                            </div>
                                                            {selectedOption.verified && (
                                                                <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-gray-100 flex items-center justify-center border border-white">
                                                                    <BadgeCheck className="w-2 h-2 text-white" />
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* Name and headline */}
                                                    <div className="mb-2">
                                                        <h4 className="text-sm font-semibold text-gray-900">
                                                            {displayName}
                                                        </h4>
                                                        {urlName && (
                                                            <div className="flex items-center gap-1.5 mb-1">
                                                                <span className="text-[15px] font-medium text-gray-700 bg-gray-100 px-1.5 py-0.5">
                                                                    {urlName.firstName}
                                                                </span>
                                                                {urlName.lastName && (
                                                                    <span className="text-[15px] font-medium text-gray-700 bg-gray-100 px-1.5 py-0.5">
                                                                        {urlName.lastName}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        )}
                                                        {headline && (
                                                            <p className="text-xs text-gray-700 flex items-center gap-1">
                                                                <Briefcase className="w-3 h-3 text-gray-400" />
                                                                {headline}
                                                            </p>
                                                        )}
                                                        {selectedOption.title && typeof selectedOption.title === 'string' && !headline && (
                                                            <p className="text-xs text-gray-700 flex items-center gap-1">
                                                                <Briefcase className="w-3 h-3 text-gray-400" />
                                                                {selectedOption.title}
                                                            </p>
                                                        )}
                                                    </div>

                                                    {/* Company info */}
                                                    {selectedOption.company && typeof selectedOption.company === 'string' && (
                                                        <div className="mb-2 flex items-center gap-1.5 text-xs text-gray-600">
                                                            <Building className="w-3 h-3 text-gray-400" />
                                                            <span className="font-medium">{selectedOption.company}</span>
                                                        </div>
                                                    )}

                                                    {/* Snippet/description */}
                                                    {snippet && (
                                                        <div className="mb-2 p-2 bg-gray-50 border border-gray-100">
                                                            <p className="text-[15px] text-gray-600">
                                                                {snippet}
                                                            </p>
                                                        </div>
                                                    )}

                                                {/* Meta info grid */}
                                                <div className="grid grid-cols-2 gap-2 mb-2">
                                                    {selectedOption.location && typeof selectedOption.location === 'string' && (
                                                        <div className="flex items-center gap-1.5 text-[15px] text-gray-600 bg-gray-50 px-2 py-1">
                                                            <MapPin className="w-3 h-3 text-gray-400 flex-shrink-0" />
                                                            <span className="truncate">{selectedOption.location}</span>
                                                        </div>
                                                    )}
                                                    {selectedOption.connections && typeof selectedOption.connections !== 'object' && (
                                                        <div className="flex items-center gap-1.5 text-[15px] text-gray-600 bg-gray-50 px-2 py-1">
                                                            <Users className="w-3 h-3 text-gray-400 flex-shrink-0" />
                                                            <span>{selectedOption.connections}+</span>
                                                        </div>
                                                    )}
                                                    {selectedOption.industry && typeof selectedOption.industry === 'string' && (
                                                        <div className="flex items-center gap-1.5 text-[15px] text-gray-600 bg-gray-50 px-2 py-1 col-span-2">
                                                            <Briefcase className="w-3 h-3 text-gray-400 flex-shrink-0" />
                                                            <span className="truncate">{selectedOption.industry}</span>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Experience/Education preview */}
                                                {(selectedOption.experience || selectedOption.education) && (
                                                    <div className="mb-2 space-y-1.5">
                                                        {selectedOption.experience && Array.isArray(selectedOption.experience) && selectedOption.experience.length > 0 && (
                                                            <div className="text-[15px]">
                                                                <p className="text-gray-500 font-medium mb-0.5 uppercase text-[9px]">Experience</p>
                                                                <div className="space-y-0.5">
                                                                    {selectedOption.experience.slice(0, 2).map((exp: any, idx: number) => (
                                                                        <div key={idx} className="flex items-start gap-1 text-gray-700">
                                                                            <span className="text-gray-400">•</span>
                                                                            <span className="flex-1">
                                                                                {typeof exp === 'string' ? exp : `${exp.title || ''} at ${exp.company || ''}`.trim()}
                                                                            </span>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}
                                                        {selectedOption.education && Array.isArray(selectedOption.education) && selectedOption.education.length > 0 && (
                                                            <div className="text-[15px]">
                                                                <p className="text-gray-500 font-medium mb-0.5 uppercase text-[9px]">Education</p>
                                                                <div className="space-y-0.5">
                                                                    {selectedOption.education.slice(0, 2).map((edu: any, idx: number) => (
                                                                        <div key={idx} className="flex items-start gap-1 text-gray-700">
                                                                            <span className="text-gray-400">•</span>
                                                                            <span className="flex-1">
                                                                                {typeof edu === 'string' ? edu : `${edu.school || ''} - ${edu.degree || ''}`.trim()}
                                                                            </span>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        );
                                    })()}

                                    {/* Rich preview for Google Place with address comparison */}
                                    {suggestion.enrichmentType === "google_place" && selectedOption && (
                                        <div className="mb-3 space-y-2">
                                            {/* Rating and review count */}
                                            {(selectedOption.rating != null || selectedOption.user_ratings_total != null) && (
                                                <div className="flex items-center gap-3 px-3 py-2 bg-yellow-50 border border-yellow-200">
                                                    {selectedOption.rating != null && (
                                                        <div className="flex items-center gap-1.5">
                                                            <span className="text-yellow-500 text-sm">★</span>
                                                            <span className="text-sm font-semibold text-gray-900">
                                                                {typeof selectedOption.rating === 'number'
                                                                    ? selectedOption.rating.toFixed(1)
                                                                    : selectedOption.rating}
                                                            </span>
                                                        </div>
                                                    )}
                                                    {selectedOption.user_ratings_total != null && (
                                                        <span className="text-xs text-gray-500">
                                                            {selectedOption.user_ratings_total.toLocaleString('fr-FR')} avis
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                            {/* Address comparison */}
                                            {suggestion.address && selectedOption.formatted_address && (
                                                <div className="border border-gray-200 bg-gray-50 p-3">
                                                    <div className="flex items-center gap-1.5 mb-2">
                                                        <ArrowLeftRight className="w-3.5 h-3.5 text-gray-500" />
                                                        <span className="text-[15px] font-medium text-gray-600 uppercase">
                                                            Comparison
                                                        </span>
                                                    </div>
                                                    <div className="grid grid-cols-2 gap-2">
                                                        <div className="bg-white p-2 border border-gray-200">
                                                            <p className="text-[9px] font-medium text-gray-500 uppercase mb-1">
                                                                Event
                                                            </p>
                                                            <p className="text-[15px] text-gray-800">
                                                                {suggestion.address}
                                                            </p>
                                                        </div>
                                                        <div className="bg-white p-1.5 border border-gray-200">
                                                            <p className="text-[8px] font-medium text-gray-500 uppercase mb-0.5">
                                                                Google
                                                            </p>
                                                            <p className="text-[15px] text-gray-800">
                                                                {typeof selectedOption.formatted_address === 'string'
                                                                    ? selectedOption.formatted_address
                                                                    : 'N/A'}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    {/* Name comparison if available */}
                                                    {((typeof selectedOption.display_name === 'string' && selectedOption.display_name) ||
                                                      (typeof selectedOption.name === 'string' && selectedOption.name)) && (
                                                        <div className="grid grid-cols-2 gap-2 mt-2">
                                                            <div className="bg-white p-2 border border-gray-200">
                                                                <p className="text-[9px] font-medium text-gray-500 uppercase mb-1">
                                                                    Event
                                                                </p>
                                                                <p className="text-[15px] text-gray-800">
                                                                    {suggestion.companyName}
                                                                    {suggestion.restaurantName && suggestion.restaurantName !== suggestion.companyName && (
                                                                        <span className="text-gray-500"> ({suggestion.restaurantName})</span>
                                                                    )}
                                                                </p>
                                                            </div>
                                                            <div className="bg-white p-1.5 border border-gray-200">
                                                                <p className="text-[8px] font-medium text-gray-500 uppercase mb-0.5">
                                                                    Google
                                                                </p>
                                                                <p className="text-[15px] text-gray-800">
                                                                    {(typeof selectedOption.display_name === 'string' && selectedOption.display_name) ||
                                                                     (typeof selectedOption.name === 'string' && selectedOption.name) ||
                                                                     'N/A'}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Rich preview for Google Place DFS (DataForSEO) */}
                                    {suggestion.enrichmentType === "google_place_DFS" && selectedOption && (
                                        <div className="mb-3 space-y-2">
                                            {/* Rating */}
                                            {selectedOption.rating != null && (
                                                <div className="flex items-center gap-3 px-3 py-2 bg-yellow-50 border border-yellow-200">
                                                    <div className="flex items-center gap-1.5">
                                                        <span className="text-yellow-500 text-sm">★</span>
                                                        <span className="text-sm font-semibold text-gray-900">
                                                            {typeof selectedOption.rating?.value === 'number'
                                                                ? selectedOption.rating.value.toFixed(1)
                                                                : typeof selectedOption.rating === 'number'
                                                                ? selectedOption.rating.toFixed(1)
                                                                : selectedOption.rating}
                                                        </span>
                                                    </div>
                                                    {(selectedOption.rating?.votes_count ?? selectedOption.user_ratings_total) != null && (
                                                        <span className="text-xs text-gray-500">
                                                            {(selectedOption.rating?.votes_count ?? selectedOption.user_ratings_total).toLocaleString('fr-FR')} avis
                                                        </span>
                                                    )}
                                                    {selectedOption.category && (
                                                        <span className="text-xs text-gray-500 italic">{selectedOption.category}</span>
                                                    )}
                                                    {selectedOption.price_level && (
                                                        <span className="text-xs text-gray-400">{selectedOption.price_level}</span>
                                                    )}
                                                </div>
                                            )}
                                            {/* Address + name comparison */}
                                            {suggestion.address && selectedOption.address && (
                                                <div className="border border-gray-200 bg-gray-50 p-3">
                                                    <div className="flex items-center gap-1.5 mb-2">
                                                        <ArrowLeftRight className="w-3.5 h-3.5 text-gray-500" />
                                                        <span className="text-[15px] font-medium text-gray-600 uppercase">
                                                            Comparison
                                                        </span>
                                                    </div>
                                                    <div className="grid grid-cols-2 gap-2">
                                                        <div className="bg-white p-2 border border-gray-200">
                                                            <p className="text-[9px] font-medium text-gray-500 uppercase mb-1">Event</p>
                                                            <p className="text-[15px] text-gray-800">{suggestion.address}</p>
                                                        </div>
                                                        <div className="bg-white p-1.5 border border-gray-200">
                                                            <p className="text-[8px] font-medium text-gray-500 uppercase mb-0.5">Google DFS</p>
                                                            <p className="text-[15px] text-gray-800">{selectedOption.address}</p>
                                                        </div>
                                                    </div>
                                                    {selectedOption.title && (
                                                        <div className="grid grid-cols-2 gap-2 mt-2">
                                                            <div className="bg-white p-2 border border-gray-200">
                                                                <p className="text-[9px] font-medium text-gray-500 uppercase mb-1">Event</p>
                                                                <p className="text-[15px] text-gray-800">
                                                                    {suggestion.companyName}
                                                                    {suggestion.restaurantName && suggestion.restaurantName !== suggestion.companyName && (
                                                                        <span className="text-gray-500"> ({suggestion.restaurantName})</span>
                                                                    )}
                                                                </p>
                                                            </div>
                                                            <div className="bg-white p-1.5 border border-gray-200">
                                                                <p className="text-[8px] font-medium text-gray-500 uppercase mb-0.5">Google DFS</p>
                                                                <p className="text-[15px] text-gray-800">{selectedOption.title}</p>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                            {/* Phone */}
                                            {selectedOption.phone && (
                                                <div className="px-3 py-1.5 bg-gray-50 border border-gray-200 text-xs text-gray-700">
                                                    📞 {selectedOption.phone}
                                                </div>
                                            )}
                                            {/* Description */}
                                            {selectedOption.description && (
                                                <div className="px-3 py-2 bg-gray-50 border border-gray-200 text-xs text-gray-600 line-clamp-3">
                                                    {selectedOption.description}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Confidence tag */}
                                    <div className="flex items-center gap-2 pt-3 border-t border-gray-100">
                                        {suggestion.enrichmentType === "google_place_DFS" ? (
                                            <span className={`text-xs font-medium px-2 py-0.5 border ${CONFIDENCE_BADGE[suggestion.aiConfidenceLevel ?? "unverified"] ?? CONFIDENCE_BADGE.unverified}`}>
                                                {suggestion.aiConfidenceLevel === "high"
                                                    ? "Haute"
                                                    : suggestion.aiConfidenceLevel === "medium"
                                                        ? "Moyenne"
                                                        : suggestion.aiConfidenceLevel === "low"
                                                            ? "Faible"
                                                            : "Non verifiee"}
                                            </span>
                                        ) : (
                                            suggestion.aiConfidenceLevel && suggestion.aiConfidenceLevel !== "unverified" && (
                                                <span className={`text-xs font-medium px-2 py-0.5 border ${CONFIDENCE_BADGE[suggestion.aiConfidenceLevel] ?? CONFIDENCE_BADGE.unverified}`}>
                                                    {suggestion.aiConfidenceLevel === "high" ? "Haute" : suggestion.aiConfidenceLevel === "medium" ? "Moyenne" : "Faible"}
                                                </span>
                                            )
                                        )}
                                        {getDaysAgo(suggestion.detectedAt) && (
                                            <span className="ml-auto flex items-center gap-1 text-xs text-gray-400">
                                                <Clock className="w-3.5 h-3.5" />
                                                {getDaysAgo(suggestion.detectedAt)}
                                            </span>
                                        )}
                                    </div>
                                </div>

                                {/* Action buttons - right side */}
                                <div className="flex flex-col border-l border-gray-200 w-24">
                                    <button
                                        onClick={() => handleApprove(suggestion, idx)}
                                        disabled={isLoading}
                                        className="flex-1 flex flex-col items-center justify-center gap-1.5 hover:bg-green-50 transition-colors group disabled:opacity-50"
                                    >
                                        <Check className="w-5 h-5 text-gray-400 group-hover:text-green-600" />
                                        <span className="text-[15px] font-medium text-gray-500 group-hover:text-green-600 uppercase">
                                            Approve
                                        </span>
                                    </button>
                                    <div className="border-t border-gray-200" />
                                    <button
                                        onClick={() => handleReject(suggestion, idx)}
                                        disabled={isLoading}
                                        className="flex-1 flex flex-col items-center justify-center gap-1.5 hover:bg-red-50 transition-colors group disabled:opacity-50"
                                    >
                                        <X className="w-5 h-5 text-gray-400 group-hover:text-red-600" />
                                        <span className="text-[15px] font-medium text-gray-500 group-hover:text-red-600 uppercase">
                                            Reject
                                        </span>
                                    </button>
                                    <div className="border-t border-gray-200" />
                                    <button
                                        onClick={() => handlePurge(suggestion, idx)}
                                        disabled={isLoading}
                                        className="flex flex-col items-center justify-center gap-1 py-2 hover:bg-[#F9F6F3] transition-colors group disabled:opacity-50"
                                    >
                                        <Trash2 className="w-3.5 h-3.5 text-gray-300 group-hover:text-gray-500" />
                                        <span className="text-[10px] font-medium text-gray-300 group-hover:text-gray-500 uppercase">
                                            Purge
                                        </span>
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Pagination */}
                {!loading && total > 50 && (
                    <div className="flex justify-center gap-1.5 mt-6">
                        <button
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                            disabled={page === 1}
                            className="px-3 py-1 border border-gray-300 text-xs font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                        >
                            Precedent
                        </button>
                        <span className="px-3 py-1 text-xs text-gray-500">
                            {page} / {Math.ceil(total / 50)}
                        </span>
                        <button
                            onClick={() => setPage((p) => p + 1)}
                            disabled={page >= Math.ceil(total / 50)}
                            className="px-3 py-1 border border-gray-300 text-xs font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                        >
                            Suivant
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
